/**
 * Centralized date utility functions for consistent date handling across the application
 * Ensures all dates are displayed in local time with consistent formatting
 */

import { createLogger } from './debugLogger';

// Create debug logger for this module
const logger = createLogger('dateUtils', 'Date formatting and timezone utilities', false, 'INFO');

/**
 * Convert any date input to a local Date object
 * Handles ISO strings, Date objects, and other date formats
 */
export function toLocalDate(dateInput) {
  if (!dateInput) return null;
  
  try {
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return null;

    // If the input is already a Date object, return it (it's already in local time)
    if (dateInput instanceof Date) return date;

    // For ISO strings or other formats, create a new Date object
    // This automatically converts to local time
    return new Date(date.getTime());
  } catch (error) {
    logger.error('Error converting to local date:', error);
    return null;
  }
}

/**
 * Parse a date for use as a sort key, falling back to the Unix epoch when
 * the input is missing or unparseable.
 *
 * `new Date(x) || fallback` looks like it falls back on a bad `x`, but a
 * Date object is always truthy -- even an Invalid Date -- so that `||`
 * never actually triggers. An Invalid Date then makes every </>/comparison
 * against it evaluate to false (NaN semantics), so a sort comparator built
 * on it silently treats that row as equal to everything, leaving it in
 * whatever position it happened to already be in instead of sorting to
 * either end. toLocalDate()'s own explicit isNaN(getTime()) check makes the
 * fallback real.
 */
export function parseDateForSort(dateInput) {
  return toLocalDate(dateInput) || new Date(0);
}

/**
 * Format date for display (date only, no time)
 * Returns formatted string in local time
 */
export function formatDate(dateInput, options = {}) {
  const date = toLocalDate(dateInput);
  if (!date) return '';
  
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
  };
  
  return date.toLocaleDateString('en-US', { ...defaultOptions, ...options });
}

/**
 * Format date and time for display
 * Returns formatted string in local time
 */
export function formatDateTime(dateInput, options = {}) {
  const date = toLocalDate(dateInput);
  if (!date) return '';
  
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
  };
  
  return date.toLocaleString('en-US', { ...defaultOptions, ...options });
}

/**
 * Format time only for display
 * Returns formatted string in local time
 */
export function formatTime(dateInput, options = {}) {
  const date = toLocalDate(dateInput);
  if (!date) return '';
  
  const defaultOptions = {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
  };
  
  return date.toLocaleTimeString('en-US', { ...defaultOptions, ...options });
}

/**
 * Format date for short display (e.g., "Apr 24")
 * Returns formatted string in local time
 */
export function formatShortDate(dateInput) {
  const date = toLocalDate(dateInput);
  if (!date) return '';
  
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
  });
}

/**
 * Format date for compact display (e.g., "04/24/2025")
 * Returns formatted string in local time
 */
export function formatCompactDate(dateInput) {
  const date = toLocalDate(dateInput);
  if (!date) return '';
  
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
  });
}

/**
 * Format date for ISO display (YYYY-MM-DD) in local time
 * Useful for form inputs and API calls
 */
export function formatISODate(dateInput) {
  const date = toLocalDate(dateInput);
  if (!date) return '';
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
}

/**
 * Format date and time for ISO display (YYYY-MM-DDTHH:mm) in local time
 * Useful for datetime form inputs
 */
export function formatISODateTime(dateInput) {
  const date = toLocalDate(dateInput);
  if (!date) return '';
  
  const dateStr = formatISODate(date);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  
  return `${dateStr}T${hours}:${minutes}`;
}

/**
 * Get current local date as ISO string (YYYY-MM-DD)
 */
export function getCurrentLocalDate() {
  return formatISODate(new Date());
}

/**
 * Get current local datetime as ISO string (YYYY-MM-DDTHH:mm)
 */
export function getCurrentLocalDateTime() {
  return formatISODateTime(new Date());
}

/**
 * Check if a date is today (in local time)
 */
export function isToday(dateInput) {
  const date = toLocalDate(dateInput);
  if (!date) return false;
  
  const today = new Date();
  return date.toDateString() === today.toDateString();
}

/**
 * Get relative time description (e.g., "2 hours ago", "yesterday")
 * Returns formatted string in local time
 */
export function getRelativeTime(dateInput) {
  const date = toLocalDate(dateInput);
  if (!date) return '';
  
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffSeconds < 60) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  
  return formatDate(date);
}

/**
 * Get the user's current timezone
 */
export function getUserTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Check if the user is in daylight saving time
 */
export function isDaylightSavingTime(dateInput = new Date()) {
  const date = toLocalDate(dateInput);
  if (!date) return false;

  const jan = new Date(date.getFullYear(), 0, 1);
  const jul = new Date(date.getFullYear(), 6, 1);
  const standardOffset = Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset());
  const dstOffset = Math.min(jan.getTimezoneOffset(), jul.getTimezoneOffset());

  // A timezone that doesn't observe DST at all (UTC, Arizona, most of Asia)
  // has jan/jul offsets equal to each other -- and to every other date's
  // offset, since it never changes. Without this guard, the comparison
  // below degenerated to "date's offset === date's offset", trivially
  // true for every date, instead of the correct answer: there's no DST to
  // be "in", so this must be false.
  if (standardOffset === dstOffset) return false;

  return date.getTimezoneOffset() === dstOffset;
}

/**
 * Check if the US stock market is currently open
 * Market hours: Monday-Friday, 9:30 AM - 4:00 PM Eastern Time
 * Returns false on weekends and outside market hours
 */
export function isMarketOpen(dateInput = new Date()) {
  const date = toLocalDate(dateInput);
  if (!date) return false;

  // Convert to Eastern Time (US stock market timezone)
  const etFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    weekday: 'short',
    hour: 'numeric',
    minute: 'numeric',
    hour12: false
  });

  const etParts = etFormatter.formatToParts(date);
  const weekday = etParts.find(p => p.type === 'weekday')?.value;
  const hour = parseInt(etParts.find(p => p.type === 'hour')?.value || '0', 10);
  const minute = parseInt(etParts.find(p => p.type === 'minute')?.value || '0', 10);

  // Check if weekend (Saturday or Sunday)
  if (weekday === 'Sat' || weekday === 'Sun') {
    return false;
  }

  // Market opens at 9:30 AM ET (9 hours, 30 minutes)
  // Market closes at 4:00 PM ET (16 hours, 0 minutes)
  const timeInMinutes = hour * 60 + minute;
  const marketOpen = 9 * 60 + 30; // 9:30 AM
  const marketClose = 16 * 60; // 4:00 PM

  return timeInMinutes >= marketOpen && timeInMinutes < marketClose;
}
